import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from '@mui/material';
import { getColor } from '../../assets/theme';
import { WarningOutlined } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { useDispatch } from 'react-redux';
import { clearPropertiesState } from '../../store/slices/property.slice';
import { removeUser } from '../../store/slices/user.slice';
import { useNavigate } from 'react-router-dom';
import { AlertState } from '../../context/app-context';
import { useAppSelector } from '@store/index';
import { setExpiredSession } from '@store/slices/api.slice';
import {
  selectMaintenanceMode,
  selectSessionExpired,
} from '@store/selector/api.selector';
import { clearAllBrowserData } from '@utils/security';

/**
 * DialogSessionExpired Component
 *
 * This component renders a dialog that appears when the user's session has expired.
 * It provides information about the session expiration and options to either cancel or log in again.
 *
 * @returns {JSX.Element} The rendered DialogSessionExpired component
 */
const DialogSessionExpired = ({
  toast,
}: {
  toast: (data: AlertState) => Promise<void>;
}) => {
  const isMaintenance = useAppSelector(selectMaintenanceMode);
  const isSessionExpired = useAppSelector(selectSessionExpired);
  const { white } = getColor();

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /**
   * Handles the "Log In Again" button click
   * Clears the properties state, removes the user, shows a toast notification,
   * closes the dialog, and navigates to the login page
   */
  const handleLogInAgain = () => {
    dispatch(clearPropertiesState());
    dispatch(removeUser());
    toast({
      message: 'Session Expired',
      severity: 'error',
      errorLevel: 2,
    });
    dispatch(setExpiredSession(false));
    clearAllBrowserData();
    navigate('/login');
  };

  return (
    <Dialog open={isSessionExpired && !isMaintenance}>
      <Stack
        sx={{
          bgcolor: white,
          borderRadius: '4px',
          p: '36px 34px 24px 34px',
          justifyContent: 'flex-start',
        }}
      >
        <Box sx={{ width: '100%' }}>
          <WarningOutlined
            sx={{
              width: '59px',
              height: '59px',
              bgcolor: '#FFAB000A',
              p: '12px',
              borderRadius: '100%',
              color: '#FFAB00',
            }}
          />
        </Box>
        <DialogTitle sx={{ p: '8px 0', fontWeight: 600 }}>
          Session Expired
        </DialogTitle>
        <DialogContent sx={{ p: '16px 0!important' }}>
          <Typography sx={{ pb: '24px' }}>
            For security reasons, your session has expired. Please log in again
            to continue.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: '0 0 8px 0' }}>
          <Stack direction='row' gap='8px'>
            <Button
              variant='outlined'
              onClick={() => dispatch(setExpiredSession(false))}
            >
              Cancel
            </Button>
            <LoadingButton
              variant='contained'
              type='submit'
              onClick={handleLogInAgain}
            >
              Log In Again
            </LoadingButton>
          </Stack>
        </DialogActions>
      </Stack>
    </Dialog>
  );
};

export default DialogSessionExpired;
